import { z } from 'zod';
/**
 * Deposit amounts (cash-deposits.md section 10, decision D14).
 *
 * Amounts cross the wire as decimal STRINGS. Every currency uses exactly five
 * decimal places; an amount with more significant decimals is refused, never
 * rounded. These helpers work on the string form only, so the web form and the
 * API apply exactly the same rule without ever passing through a JavaScript
 * float.
 */
/** Decimal places of every amount in every currency (decision D14). */
export const AMOUNT_DECIMALS = 5;
/** Largest number of integer digits an amount may carry. */
export const MAX_AMOUNT_INTEGER_DIGITS = 15;
/** Shape only: the decimal-places rule is checked by {@link amountFitsDecimals}. */
const AMOUNT_PATTERN = new RegExp(`^\\d{1,${MAX_AMOUNT_INTEGER_DIGITS}}(\\.\\d+)?$`);
/**
 * A positive amount as typed by an employee: digits, an optional dot and
 * decimals. Having more than five significant decimals is a separate refusal
 * (`AMOUNT_DECIMALS_EXCEEDED`) checked with {@link amountFitsDecimals}.
 */
export const amountInputSchema = z
    .string()
    .trim()
    .regex(AMOUNT_PATTERN, 'amount must be a positive decimal string such as 150.25000')
    .refine((value) => isPositiveAmount(value), 'amount must be greater than zero');
/** An amount returned by the API, always scaled to five decimals. */
export const amountSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'must be a decimal string');
/** Number of significant fraction digits (trailing zeros carry no value). */
export function significantDecimals(amount) {
    const [, fraction = ''] = amount.trim().split('.');
    return fraction.replace(/0+$/, '').length;
}
/** True when the amount has no more than five significant decimals. */
export function amountFitsDecimals(amount) {
    return significantDecimals(amount) <= AMOUNT_DECIMALS;
}
/** True when the decimal string is strictly greater than zero. */
export function isPositiveAmount(amount) {
    const value = amount.trim();
    return !value.startsWith('-') && /[1-9]/.test(value);
}
/**
 * Canonical form of an amount: no leading zeros and exactly five fraction
 * digits ("7.5" becomes "7.50000"). The caller must first check
 * {@link amountFitsDecimals}; digits beyond five are an error here, never a
 * rounding.
 */
export function formatAmount(amount) {
    if (!amountFitsDecimals(amount)) {
        throw new RangeError(`amount has more than ${AMOUNT_DECIMALS} significant decimals`);
    }
    const [rawInteger = '0', rawFraction = ''] = amount.trim().split('.');
    const integer = rawInteger.replace(/^0+(?=\d)/, '');
    const fraction = rawFraction.replace(/0+$/, '').padEnd(AMOUNT_DECIMALS, '0');
    return `${integer}.${fraction}`;
}
//# sourceMappingURL=amount.js.map